import { PlantsList } from './plants-list.tsx';
import { PlantsWithOverview } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import {
  type PlantsListViewFilterState,
  PlantsListViewFilterCommand,
} from './plants-list-view-filter-command.tsx';
import { Archive, LayoutGrid, Table } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { usePlantsViewFilterStore } from '@/services/store/use-plants-view-filter-store.ts';

type LayoutType = 'grid' | 'table';

export const PlantsView = ({
  plants,
  isLoading,
}: {
  plants: PlantsWithOverview[];
  isLoading: boolean;
}) => {
  const store = usePlantsViewFilterStore();

  const handleStateChange = (newState: PlantsListViewFilterState) => {
    store.setFilterState(newState);
  };

  const handleLayoutChange = (layout: LayoutType) => {
    store.setLayout(layout);
  };

  const filteredPlants = plants?.filter((p) => {
    if (!store.showArchived && p.basicDetails.status === 'Archived') {
      return false;
    }

    if (store.setupType === 'production-only') {
      return p.technicalSpecifications.setupType === 'production-only';
    }

    if (store.setupType === 'storage-only') {
      return p.technicalSpecifications.setupType === 'storage-only';
    }

    if (store.setupType === 'production-storage') {
      return p.technicalSpecifications.setupType === 'production-storage';
    }

    if (store.setupType === 'all') {
      if (store.view === 'production') {
        return (
          p.technicalSpecifications.setupType === 'production-storage' ||
          p.technicalSpecifications.setupType === 'production-only'
        );
      }

      if (store.view === 'storage') {
        return (
          p.technicalSpecifications.setupType === 'production-storage' ||
          p.technicalSpecifications.setupType === 'storage-only'
        );
      }

      return true;
    }

    return true;
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-[36px] w-[215px] rounded-md bg-white/80"></div>
        <ul className="grid grid-cols-1 gap-6 min-[1101px]:grid-cols-2 min-[1651px]:grid-cols-3">
          {Array(6)
            .fill(() => null)
            .map((_, index) => (
              <li
                key={index}
                className="plant-grid-item h-[176px] overflow-hidden rounded-xl bg-white/80"
              >
                <div className="w-full"></div>
              </li>
            ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <PlantsListViewFilterCommand
          currentState={{
            setupType: store.setupType,
            view: store.view,
          }}
          onStateChange={handleStateChange}
        />
        <div className="flex rounded-md border border-gray-200 bg-white">
          <Button
            title="Card view"
            variant="ghost"
            size="sm"
            onClick={() => handleLayoutChange('grid')}
            className={`min-h-9 rounded-r-none px-3 ${
              store.layout === 'grid' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            title="Table view"
            variant="ghost"
            size="sm"
            onClick={() => handleLayoutChange('table')}
            className={`min-h-9 rounded-l-none border-l border-gray-200 px-3 ${
              store.layout === 'table' ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Table className="h-4 w-4" />
          </Button>
        </div>
        <Button
          title={store.showArchived ? 'Hide Archive' : 'Show Archive'}
          variant="ghost"
          size="sm"
          onClick={() => store.setShowArchived(!store.showArchived)}
          className={`min-h-9 gap-2 rounded-md border border-gray-200 bg-white px-3 ${
            store.showArchived ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Archive className="h-4 w-4" />
        </Button>
      </div>
      <PlantsList
        plants={filteredPlants || []}
        viewFilterState={{ view: store.view, setupType: store.setupType }}
        layout={store.layout}
      />
    </div>
  );
};
